import {
  InputGroup as ChakraInputGroup,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  HStack,
  InputRightElement,
  Radio,
  Select,
  Textarea,
} from '@chakra-ui/react'
import { SelectFormControl } from 'BrokerTsLib/src/components'
import type { InsuranceParamsDto } from 'BrokerTsLib/src/dtos'
import { Insurer, SelectionType } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect, useMemo, useState } from 'react'

import { FormLabel } from '@/components/FormLabel'
import { InputGroup } from '@/components/InputGroup'
import { NumberInput } from '@/components/ui/NumberInput'
import { ReactHookFormDatepicker as Datepicker } from '@/containers/forms/Datepicker'
import { FormBody } from '@/containers/forms/FormBody'
import { FormRow } from '@/containers/forms/FormRow'
import { RadioGroup } from '@/containers/forms/RadioGroup'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useSelectedQuote } from '@/context/SelectedQuoteContext'
import { useSelections } from '@/hooks'
import { useGetHomeServiceCities, useGetHomeServices, useGetVehicleInspectionDistricts } from '@/hooks/useWizard'

import { usePartner } from '@/context/PartnerContext'
import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'
import { formatLicensePlate } from '@/utils/formatLicencePlate'
import { InvalidVinWarning } from '../../../InvalidVinWarning'
import type { Step3FormValues } from '../../Step3'

export const VehicleAdditionalInfoForm = () => {
  const { app } = useApp()
  const extraWizard = app == Apps.extra

  const { currentPartner } = usePartner()
  const vinMaxLength = 17
  const { insuranceParams, isDisabled } = useInsuranceParams()
  const { offer } = useSelectedQuote()
  const ipv = insuranceParams as InsuranceParamsDto

  const methods = useFormContext<Step3FormValues>()
  const {
    formState: { errors },
    register,
    getValues,
    watch,
    trigger,
    control
  } = methods

  const vin = watch('vehicle.vin')
  const disabledVin = useMemo(() => !!ipv.vehicle.vin || isDisabled, [])

  const licensePlateTypes = getValues('vehicle.licensePlateTypes')
  const { data: dataPurchaseMethod } = useSelections(
    SelectionType.PurchaseMethod,
  )
  const { data: inspectionDistrictsData, refetch: refetchInspectionDistricts } = useGetVehicleInspectionDistricts(offer?.offerId?.toString() ?? '', { enabled: false })
  const { data: homeServiceCities, refetch: refetchHomeServiceCities, isLoading: isLoadingHomeServiceCities } = useGetHomeServiceCities(offer?.offerId?.toString() ?? '', { enabled: false })

  useEffect(() => {
    if (offer?.offerId) {
      refetchInspectionDistricts()
    }
    if (offer?.insurerId == Insurer.GeneraliCeskaPojistovna && currentPartner?.partner != 'Jap') {
      refetchHomeServiceCities()
    }
  }, [offer])

  const maxSpeedRequired = getValues('vehicle.isVehicleMaxSpeedRequired')

  const homeServiceCity = watch('vehicle.homeServiceCity')
  const { data: homeServices, isLoading: isLoadingHomeServices } = useGetHomeServices(offer?.offerId?.toString() ?? '', homeServiceCity ?? '')

  const handleCopy = (event: any) => {
    event.preventDefault()
    const selectedText = window.getSelection()?.toString()
    // odebrání mezery ze zkopírovaného textu
    const textWithoutSpaces = selectedText?.replace(/\s/g, '')
    event.clipboardData.setData('text/plain', textWithoutSpaces)
  }

  const inspectionDateRequired = watch('vehicle.isInspectionDateRequired')
  const otherPersonByInspection = watch('vehicle.otherPersonByInspection')

  const licensePlate = watch('vehicle.licensePlate')
  const cisloTechnickehoPrukazu = watch('vehicle.cisloTechnickehoPrukazu')
  const cisloOrv = watch('vehicle.cisloOrv')

  const showDamageFields = offer?.insurerId != Insurer.Kooperativa

  const [vinInputFinished, setVinInputFinished] = useState(false)
  
  return (
    <>
      <FormBody>
        <FormRow itemsCount={2}>
          <FormControl isInvalid={!!errors.vehicle?.licensePlate}>
            <FormLabel>Registrační značka</FormLabel>

            <InputGroup
              {...register('vehicle.licensePlate', {
                onBlur: () => { trigger('vehicle.vin') },
                onChange: (e) => {
                  (e.target.value = formatLicensePlate(e.target.value) ?? '')
                  trigger('vehicle.cisloTechnickehoPrukazu')
                  trigger('vehicle.cisloOrv')
                },
              })}
              isDisabled={isDisabled}
              minLength={7}
              maxLength={9}
              placeholder="### ####"
              value={formatLicensePlate(licensePlate)}
              // formHelperText='Zadejte registrační značku bez mezer a pomlček.'
              errors={errors.vehicle?.licensePlate}
              onCopy={handleCopy}
            />
          </FormControl>

          <FormControl isInvalid={!!errors.vehicle?.vin}>
            <FormLabel>VIN</FormLabel>

            <InputGroup
              {...register('vehicle.vin', { 
                onBlur: () => {
                  if (!vinInputFinished)
                    setVinInputFinished(true)
                },
                onChange: () => {
                  if (vinInputFinished)
                    setVinInputFinished(false)
                },
              })}
              isDisabled={disabledVin}
              //minLength={vinMaxLength} zrušena minimální délka, případné doplnění na 17 znaků se provádí v builderu podle specifik pojišťoven
              maxLength={vinMaxLength}
              sx={{ width: '100%' }}
              rightElement={
                <InputRightElement sx={{ minW: '4rem' }}>
                  {vin?.length ?? 0}/{vinMaxLength}
                </InputRightElement>
              }
              errors={errors.vehicle?.vin}
            />
          </FormControl>
          
          <InvalidVinWarning vin={vin ?? ''} canShow={vinInputFinished} />
        </FormRow>

        {!extraWizard && <>
          <FormRow itemsCount={2}>
            <FormControl isInvalid={!!errors.vehicle?.cisloTechnickehoPrukazu} isRequired={!!licensePlate && !cisloOrv}>
              <FormLabel>Číslo velkého TP</FormLabel>

              <InputGroup
                {...register('vehicle.cisloTechnickehoPrukazu', {
                  onChange: (e) => {
                    (e.target.value = e.target.value?.toUpperCase() ?? '')
                    trigger('vehicle.cisloOrv')
                  },
                })}
                isDisabled={isDisabled}
                errors={errors.vehicle?.cisloTechnickehoPrukazu}
              />
            </FormControl>

            <FormControl isInvalid={!!errors.vehicle?.cisloOrv} isRequired={!!licensePlate && !cisloTechnickehoPrukazu}>
              <FormLabel>Číslo ORV</FormLabel>

              <InputGroup
                {...register('vehicle.cisloOrv', {
                  onChange: (e) => {
                    (e.target.value = e.target.value?.toUpperCase() ?? '')
                    trigger('vehicle.cisloTechnickehoPrukazu')
                  },
                })}
                isDisabled={isDisabled}
                errors={errors.vehicle?.cisloOrv}
              />
            </FormControl>
          </FormRow>



          <FormRow>
            {getValues('vehicle.isLicencePlateTypeRequired') && (
              <FormControl
                isInvalid={!!errors.vehicle?.licensePlateTypeId}
                isRequired
              >
                <FormLabel>Druh registrační značky (SPZ)</FormLabel>

                <Select
                  {...register('vehicle.licensePlateTypeId')}
                  isDisabled={isDisabled}
                  value={watch('vehicle.licensePlateTypeId')?.toString() ?? ''}
                >
                  <option disabled value="">
                    Vyberte
                  </option>
                  {licensePlateTypes &&
                    licensePlateTypes?.map((item) => (
                      <option value={item.id.toString()} key={item.id}>
                        {item.description}
                      </option>
                    ))}
                </Select>

                {errors.vehicle?.licensePlateTypeId && (
                  <FormErrorMessage>
                    {errors.vehicle.licensePlateTypeId.message}
                  </FormErrorMessage>
                )}
              </FormControl>
            )}

            {offer?.insurerId == Insurer.CPP && (
              <FormControl isInvalid={!!errors.vehicle?.purchaseMethod}>
                <FormLabel>Způsob pořízení vozidla</FormLabel>

                <Select
                  id="vehicle.purchaseMethod"
                  {...register('vehicle.purchaseMethod')}
                  isDisabled={isDisabled}
                  defaultValue={getValues('vehicle.purchaseMethod') ?? ''}
                >
                  <option disabled value="">
                    Vyberte
                  </option>
                  {dataPurchaseMethod &&
                    dataPurchaseMethod?.data.map((item) => (
                      <option value={item.value?.toString()} key={item.id}>
                        {item.description}
                      </option>
                    ))}
                </Select>

                {errors.vehicle?.purchaseMethod && (
                  <FormErrorMessage>
                    {errors.vehicle.purchaseMethod.message}
                  </FormErrorMessage>
                )}
              </FormControl>
            )}

            <FormControl isInvalid={!!errors.vehicle?.typeDescription}>
              <FormLabel>Bližší specifikace vozidla</FormLabel>

              <InputGroup
                {...register('vehicle.typeDescription')}
                isDisabled={isDisabled}
                placeholder="Např. detailní model, motorizace apod."
                errors={errors.vehicle?.typeDescription}
              />
            </FormControl>

            {inspectionDateRequired && (
              <FormControl
                isInvalid={!!errors.vehicle?.inspectionDate}
                isRequired={inspectionDateRequired}
              >
                <FormLabel htmlFor="vehicle.inspectionDate">
                  Datum prohlídky vozidla
                </FormLabel>
                <Datepicker
                  isDisabled={isDisabled}
                  name="vehicle.inspectionDate"
                />
              </FormControl>
            )}

            {getValues('vehicle.isInspectionDistrictRequired') &&
              inspectionDistrictsData?.data && (
              <FormControl isInvalid={!!errors.vehicle?.inspectionDistrict}>
                <FormLabel htmlFor="vehicle.inspectionDistrict">
                  Místo prohlídky
                </FormLabel>

                <Select
                  id="vehicle.inspectionDistrict"
                  {...register('vehicle.inspectionDistrict')}
                  isDisabled={isDisabled}
                >
                  <option disabled value="">
                    Vyberte
                  </option>
                  {inspectionDistrictsData?.data &&
                      inspectionDistrictsData?.data.map((item) => (
                        <option value={item.value!.toString()} key={item.value}>
                          {item.name}
                        </option>
                      ))}
                </Select>

                {errors.vehicle?.inspectionDistrict && (
                  <FormErrorMessage>
                    {errors.vehicle.inspectionDistrict.message}
                  </FormErrorMessage>
                )}
              </FormControl>
            )}

            {maxSpeedRequired && (
              <FormControl
                isInvalid={!!errors.vehicle?.vehicleMaxSpeed}
                isRequired
              >
                <FormLabel>Max. konstrukční rychlost</FormLabel>

                <ChakraInputGroup>
                  <NumberInput
                    placeholder="Např. 25"
                    {...register('vehicle.vehicleMaxSpeed')}
                    isDisabled={isDisabled}
                  />
                  <InputRightElement>km/h</InputRightElement>
                </ChakraInputGroup>

                {errors.vehicle?.vehicleMaxSpeed && (
                  <FormErrorMessage>
                    {errors.vehicle.vehicleMaxSpeed.message}
                  </FormErrorMessage>
                )}
              </FormControl>
            )}

            {[Insurer.CPP, Insurer.PVZP].includes(offer?.insurerId ?? 0) && (
              <FormControl
                isInvalid={!!errors.vehicle?.firstOwner}
                isDisabled={isDisabled}
              >
                <FormLabel htmlFor="firstOwner">První majitel</FormLabel>

                <FormHelperText>
                  Vyberte, zda je současný majitel prvním majitelem vozidla.
                </FormHelperText>

                <RadioGroup
                  name="vehicle.firstOwner"
                  onChangeValueTransform={(val) => val == '1'}
                  valueTransform={(val) =>
                    val == true ? '1' : val === false ? '0' : undefined
                  }
                  children={
                    <HStack align="start" gap={8}>
                      <Radio value="0">Ne</Radio>
                      <Radio value="1">Ano</Radio>
                    </HStack>
                  }
                />

                {errors.vehicle?.firstOwner && (
                  <FormErrorMessage>
                    {errors.vehicle.firstOwner.message}
                  </FormErrorMessage>
                )}
              </FormControl>
            )}

            {(offer?.insurerId ?? 0) == Insurer.CPP && getValues('vehicle.isInspectionRequired') && (
              <FormControl
                isInvalid={!!errors.vehicle?.otherPersonByInspection}
                isDisabled={isDisabled}
              >
                <FormLabel htmlFor="otherPersonByInspection">Jiná osoba u prohlídky</FormLabel>

                <FormHelperText>
                  Vyberte, zda u prohlídky vozidla bude přítomna jiná osoba než pojistník.
                </FormHelperText>

                <RadioGroup
                  name="vehicle.otherPersonByInspection"
                  onChangeValueTransform={(val) => val == '1'}
                  valueTransform={(val) =>
                    val == true ? '1' : val === false ? '0' : undefined
                  }
                  children={
                    <HStack align="start" gap={8}>
                      <Radio value="0">Ne</Radio>
                      <Radio value="1">Ano</Radio>
                    </HStack>
                  }
                />

                {errors.vehicle?.otherPersonByInspection && (
                  <FormErrorMessage>
                    {errors.vehicle.otherPersonByInspection.message}
                  </FormErrorMessage>
                )}
              </FormControl>
            )}

            {otherPersonByInspection && (
              <FormControl isInvalid={!!errors.vehicle?.otherPersonPhone} isRequired>
                <FormLabel >
                  Mobilní telefon kontaktní osoby
                </FormLabel>

                <FormHelperText>
                  Číslo mobilního telefonu osoby, která bude přítomna při prohlídce vozidla.
                </FormHelperText>

                <InputGroup
                  id="otherPersonPhone"
                  htmlSize={10}
                  placeholder="Telefonní číslo"
                  pattern="[\+0-9\s]+"
                  {...register('vehicle.otherPersonPhone')}
                  isDisabled={isDisabled}
                  errors={errors.vehicle?.otherPersonPhone}
                />
              </FormControl>
            )}

            {showDamageFields && (
              <FormControl>
                <FormLabel>Poškození vozidla</FormLabel>

                <CheckboxGroup isDisabled={isDisabled}>
                  <Checkbox {...register('vehicle.isDamaged')}>
                    Vozidlo je v době sjednání pojištění poškozeno.
                  </Checkbox>
                  <Checkbox {...register('vehicle.hadSevereDamageInPast')}>
                    {' '}
                    Vozidlo bylo v minulosti vážněji poškozeno
                  </Checkbox>
                </CheckboxGroup>
              </FormControl>
            )}
          </FormRow>

          {showDamageFields && (
            <FormControl variant="fullWidth">
              <FormLabel>Bližší popis poškození</FormLabel>

              <InputGroup
                {...register('vehicle.damageDescription')}
                as={Textarea}
                isDisabled={isDisabled}
                placeholder="Např. Oděrka na pravém předním blatníku..."
              />
            </FormControl>
          )}

          {getValues('vehicle.isPreferredInspectionTypeRequired') &&
            offer?.insurerId == Insurer.Kooperativa && (
            <FormControl isInvalid={!!errors.vehicle?.preferredInspectionType}>
              <FormLabel>Preferovaný typ prohlídky</FormLabel>

              <Select
                id="vehicle.preferredInspectionType"
                {...register('vehicle.preferredInspectionType')}
                isDisabled={isDisabled}
                defaultValue={
                  getValues('vehicle.preferredInspectionType') ?? ''
                }
              >
                <option value="P">fotodokumentace vozidla pojistníkem</option>
                <option value="I">prohlídka technikem Global Expert</option>
              </Select>

              {errors.vehicle?.preferredInspectionType && (
                <FormErrorMessage>
                  {errors.vehicle.preferredInspectionType.message}
                </FormErrorMessage>
              )}
            </FormControl>
          )}

          {getValues('vehicle.isInvoiceInfoRequired') && (
            <FormRow itemsCount={3}>
              <FormControl isInvalid={!!errors.vehicle?.invoiceNumber} isRequired>
                <FormLabel tooltip="UNIQA vyžaduje doplnění údajů o faktuře, pokud byla z ní určena cena vozidla.">
                  Číslo faktury
                </FormLabel>

                <InputGroup
                  id="invoiceNumber"
                  htmlSize={10}
                  {...register('vehicle.invoiceNumber')}
                  isDisabled={isDisabled}
                  errors={errors.vehicle?.invoiceNumber}
                />
              </FormControl>

              <FormControl isInvalid={!!errors.vehicle?.invoiceDate} isRequired>
                <FormLabel htmlFor="vehicle.invoiceDate">
                  Datum vystavení faktury
                </FormLabel>
                <Datepicker isDisabled={isDisabled} name="vehicle.invoiceDate" />
              </FormControl>

              <FormControl
                isInvalid={!!errors.vehicle?.invoiceSelerIco}
                isRequired
              >
                <FormLabel>IČO prodejce</FormLabel>

                <InputGroup
                  id="invoiceSelerIco"
                  htmlSize={10}
                  {...register('vehicle.invoiceSelerIco')}
                  isDisabled={isDisabled}
                  errors={errors.vehicle?.invoiceSelerIco}
                />
              </FormControl>
            </FormRow>
          )}

          {getValues('vehicle.isHomeServiceRequired') && (
            <>
              {currentPartner?.partner != 'Jap' &&
                <SelectFormControl name='vehicle.homeServiceCity' control={control}
                  label='Domovský servis - obec'
                  isDisabled={isDisabled}
                  isLoading={isLoadingHomeServiceCities}
                  isRequired
                  unselectedText='Vyberte'
                  options={homeServiceCities?.data?.map((item) => ({ value: item.name, label: item.value! }))}
                />
              }

              <SelectFormControl name='vehicle.homeServiceId' control={control}
                label='Domovský servis'
                isDisabled={isDisabled}
                isLoading={isLoadingHomeServices}
                isRequired
                unselectedText='Vyberte'
                options={homeServices?.data?.map((item) => ({ value: item.name, label: item.value! }))}
              />
            </>
          )}


        </>
        }
      </FormBody>
    </>
  )
}
